import { createRoot } from "react-dom/client";
import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { appRouteFromPathname, type AppRoute } from "../app-routes.js";
import { loadingPageState } from "./loading-state.js";
import { reloadOffersRoute } from "./offers-route-recovery.js";
import { VisualIcon, type VisualIconName } from "./VisualIcon.js";
import {
  useAnalytics,
  useBilling,
  useConfigurationSummary,
  useFunctionOwner,
  dashboardReadiness,
  type AppReadiness,
} from "./app-state.js";
import { queryClient } from "./query-client.js";
import { OfferManagerSkeleton, OfferTemplateGallerySkeleton } from "./LoadingSkeleton.js";
import "./styles.css";

type AppNavigate = (href: string) => Promise<void>;
const OFFER_WIDGET_THEME_EDITOR_URL =
  "shopify:admin/themes/current/editor?template=product&addAppBlockId=a51e7312cbc2ac95a738deb05be3e6e2/offer-widget&target=mainSection";
const OffersPage = lazy(() => import("./OffersPage.js"));
async function confirmShopifyLeave(): Promise<void> {
  const shopify: unknown = Reflect.get(window, "shopify");
  const saveBar =
    typeof shopify === "object" && shopify !== null
      ? (Reflect.get(shopify, "saveBar") as { leaveConfirmation?: () => Promise<void> })
      : undefined;
  await saveBar?.leaveConfirmation?.();
}

function AppNavigation({ onNavigate }: { onNavigate: AppNavigate }) {
  const items: Array<{ label: string; route: AppRoute }> = [
    { label: "Home", route: "/" },
    { label: "Offers", route: "/offers" },
    { label: "Analytics", route: "/analytics" },
    { label: "Online store", route: "/online-store" },
    { label: "Settings", route: "/settings" },
  ];
  return (
    <s-app-nav>
      {items.map(({ label, route }) => {
        const linkProps = route === "/" ? { href: route, rel: "home" } : { href: route };
        return (
          <s-link
            {...linkProps}
            key={route}
            onClick={(event) => {
              event.preventDefault();
              void onNavigate(route);
            }}
          >
            {label}
          </s-link>
        );
      })}
    </s-app-nav>
  );
}

function StatusPill({ tone, children }: { tone?: "success" | "warning"; children: ReactNode }) {
  const className = tone ? `sp-status sp-status--${tone}` : "sp-status";
  return <span className={className}>{children}</span>;
}

function MetricCard({
  icon,
  value,
  label,
  status,
}: {
  icon: Extract<VisualIconName, "discount" | "trend" | "money" | "eye" | "cart">;
  value: string;
  label: string;
  status?: ReactNode;
}) {
  return (
    <div className="sp-metric">
      <div className="sp-metric__top">
        <span className="sp-icon-well">
          <VisualIcon name={icon} />
        </span>
        {status}
      </div>
      <div className="sp-metric__value">{value}</div>
      <div className="sp-metric__label">{label}</div>
    </div>
  );
}

function SetupRow({ done, label, note }: { done: boolean; label: string; note: string }) {
  return (
    <div className="sp-check-row">
      <div className="sp-check-row__label">
        <span className={`sp-check-dot${done ? "" : " sp-check-dot--pending"}`}>
          {done ? "✓" : ""}
        </span>
        <span>{label}</span>
      </div>
      <span className="sp-help">{note}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="sp-detail-row">
      <span>{label}</span>
      <span className="sp-help">{value}</span>
    </div>
  );
}

const homeOfferTemplates: Array<{ anchor: string; icon: VisualIconName; title: string }> = [
  { anchor: "component-discount", icon: "discount", title: "Product discount" },
  { anchor: "volume-tiers", icon: "trend", title: "Buy more, save more" },
  { anchor: "fixed-price-bundle", icon: "package", title: "Fixed-price bundle" },
  { anchor: "free-gift", icon: "gift", title: "Free gift" },
  { anchor: "bundle-merge", icon: "bundle", title: "Percentage bundle" },
];

function HomeTemplateGallery({ onNavigate }: { onNavigate: AppNavigate }) {
  return (
    <div className="sp-template-grid">
      {homeOfferTemplates.map((template) => {
        const href = `/offers?create=${template.anchor}#${template.anchor}`;
        return (
          <a
            className="sp-template-card sp-template-link"
            href={href}
            key={template.title}
            onClick={(event) => {
              event.preventDefault();
              void onNavigate(href);
            }}
          >
            <span className="sp-icon-well">
              <VisualIcon name={template.icon} />
            </span>
            <h3>{template.title}</h3>
          </a>
        );
      })}
    </div>
  );
}

function DashboardPage({
  readiness,
  onNavigate,
}: {
  readiness: AppReadiness;
  onNavigate: AppNavigate;
}) {
  const setupComplete = [
    readiness.bundleEngineReady,
    readiness.discountEngineReady,
    readiness.totalOffers > 0,
  ].filter(Boolean).length;
  const pendingSetup = setupComplete < 3;
  return (
    <s-page heading="Stackproof Bundles" inlineSize="large">
      <div className="sp-page-stack">
        <div className="sp-home-summary">
          <div className="sp-home-summary__content">
            <span className="sp-icon-well sp-icon-well--large">
              <VisualIcon name="shield" />
            </span>
            <div>
              <h2>
                {readiness.activeOffers} live offer{readiness.activeOffers === 1 ? "" : "s"}
              </h2>
              <p>
                {readiness.totalOffers} offer{readiness.totalOffers === 1 ? "" : "s"} configured
              </p>
            </div>
          </div>
          <div className="sp-actions">
            <s-button
              href="/offers"
              onClick={(event) => {
                event.preventDefault();
                void onNavigate("/offers");
              }}
            >
              View offers
            </s-button>
            <s-button
              variant="primary"
              href="/offers"
              onClick={(event) => {
                event.preventDefault();
                void onNavigate("/offers");
              }}
            >
              Create offer
            </s-button>
          </div>
        </div>
        <div className="sp-card">
          <div className="sp-section-heading">
            <h3>Create offer</h3>
            <s-button
              variant="tertiary"
              href="/offers"
              onClick={(event) => {
                event.preventDefault();
                void onNavigate("/offers");
              }}
            >
              Manage offers
            </s-button>
          </div>
          <HomeTemplateGallery onNavigate={onNavigate} />
        </div>
        {pendingSetup && (
          <div className="sp-card">
            <div className="sp-section-heading">
              <h3>Setup</h3>
              <StatusPill tone="warning">{setupComplete} of 3</StatusPill>
            </div>
            <div className="sp-check-list">
              <SetupRow
                done={readiness.bundleEngineReady}
                label="Bundle engine"
                note={readiness.bundleEngineReady ? "Connected" : "Needs attention"}
              />
              <SetupRow
                done={readiness.discountEngineReady}
                label="Discount engine"
                note={readiness.discountEngineReady ? "Connected" : "Needs attention"}
              />
              <SetupRow
                done={readiness.totalOffers > 0}
                label="First offer"
                note={readiness.totalOffers > 0 ? "Created" : "Create one now"}
              />
            </div>
          </div>
        )}
      </div>
    </s-page>
  );
}

function formatRevenue(value: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(value);
}

function AnalyticsPage({ onNavigate }: { onNavigate: AppNavigate }) {
  const analytics = useAnalytics();
  if (analytics.status === "loading") return <AppLoadingPage route="/analytics" />;
  if (analytics.status === "error") {
    return (
      <s-page heading="Analytics" inlineSize="large">
        <s-banner tone="critical">Analytics could not be loaded. {analytics.message}</s-banner>
      </s-page>
    );
  }

  const summary = analytics.summary;
  const maximumDayViews = Math.max(1, ...summary.days.map((day) => day.views));
  const trackingActive = analytics.trackingStatus === "active";
  return (
    <s-page heading="Analytics" inlineSize="large">
      <div className="sp-page-stack">
        <div className="sp-metric-grid">
          <MetricCard icon="eye" value={String(summary.views)} label="offer views" />
          <MetricCard
            icon="cart"
            value={String(summary.additions)}
            label="adds to cart"
            status={
              summary.addToCartRate !== null ? (
                <StatusPill tone="success">{summary.addToCartRate}%</StatusPill>
              ) : undefined
            }
          />
          <MetricCard
            icon="money"
            value={
              summary.revenueByCurrency.length === 1
                ? formatRevenue(
                    summary.revenueByCurrency[0].amount,
                    summary.revenueByCurrency[0].currencyCode,
                  )
                : summary.revenueByCurrency.length > 1
                  ? `${summary.revenueByCurrency.length} currencies`
                  : "N/A"
            }
            label="attributed revenue"
            status={
              summary.orders > 0 ? (
                <StatusPill tone="success">{summary.orders} orders</StatusPill>
              ) : undefined
            }
          />
        </div>
        <div className="sp-card">
          <div className="sp-section-heading">
            <h3>Last 30 days</h3>
            {!trackingActive && <StatusPill tone="warning">Action required</StatusPill>}
          </div>
          {summary.views === 0 && (
            <div className="sp-empty-chart">
              <div>
                <VisualIcon name="trend" large />
                <h3>No data yet</h3>
                <p>Views and orders will appear here.</p>
                <s-button
                  href="/online-store"
                  onClick={(event) => {
                    event.preventDefault();
                    void onNavigate("/online-store");
                  }}
                >
                  Check online store
                </s-button>
              </div>
            </div>
          )}
          {summary.views > 0 && (
            <>
              <div className="sp-analytics-layout">
                <div className="sp-funnel" aria-label="Offer conversion funnel">
                  {[
                    { label: "Viewed", value: summary.views, icon: "eye" as const },
                    { label: "Selected", value: summary.selections, icon: "discount" as const },
                    { label: "Added", value: summary.additions, icon: "cart" as const },
                    { label: "Ordered", value: summary.orders, icon: "money" as const },
                  ].map((step, index) => (
                    <div
                      className="sp-funnel__step"
                      key={step.label}
                      style={{ width: `${Math.max(46, 100 - index * 12)}%` }}
                    >
                      <span className="sp-icon-well">
                        <VisualIcon name={step.icon} />
                      </span>
                      <span>{step.label}</span>
                      <strong>{step.value}</strong>
                    </div>
                  ))}
                </div>
                <div className="sp-chart" aria-label="Daily offer views and cart additions">
                  {summary.days.map((day, index) => (
                    <div
                      className="sp-chart__day"
                      key={day.date}
                      title={`${day.date}: ${day.views} views, ${day.additions} additions`}
                    >
                      <div className="sp-chart__bars">
                        <span
                          className="sp-chart__bar"
                          style={{ height: `${Math.max(5, (day.views / maximumDayViews) * 100)}%` }}
                        />
                        <span
                          className="sp-chart__bar sp-chart__bar--accent"
                          style={{
                            height: `${Math.max(3, (day.additions / maximumDayViews) * 100)}%`,
                          }}
                        />
                      </div>
                      <span>
                        {index % 5 === 0 || index === summary.days.length - 1
                          ? day.date.slice(8)
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sp-chart-legend">
                <span>
                  <i /> Views
                </span>
                <span>
                  <i className="sp-chart-legend__accent" /> Added
                </span>
                <span>{summary.selectionRate ?? 0}% selection rate</span>
              </div>
              {summary.revenueByCurrency.length > 1 && (
                <div className="sp-revenue-breakdown">
                  Revenue by checkout currency:{" "}
                  {summary.revenueByCurrency
                    .map((row) => formatRevenue(row.amount, row.currencyCode))
                    .join(", ")}
                </div>
              )}
            </>
          )}
        </div>
        {analytics.status === "loaded" && analytics.trackingStatus === "setup-required" && (
          <s-banner tone="warning">
            Tracking setup could not be completed. Approve the requested Shopify permissions, then
            reload this page. If they are already approved, retry in a few minutes.
          </s-banner>
        )}
        {analytics.status === "loaded" && analytics.trackingStatus === "subscription-required" && (
          <s-banner tone="warning">
            An active Stackproof subscription is required to activate tracking.
          </s-banner>
        )}
        <div className="sp-analytics-note">
          7-day attribution. No customer or order data stored.
        </div>
      </div>
    </s-page>
  );
}

function OfferPreview() {
  return (
    <div className="sp-preview-shell">
      <div className="sp-preview-shell__label">
        <VisualIcon name="eye" /> Preview
      </div>
      <div className="sp-preview-shell__product">
        <div className="sp-preview-product-image">
          <VisualIcon name="product" large />
        </div>
        <div>
          <h3>Weekend essential</h3>
          <span className="sp-help">Choose your offer</span>
        </div>
      </div>
      <div className="sp-preview-options">
        <div className="sp-preview-option">
          <div className="sp-preview-option__top">
            <span>
              <span className="sp-preview-radio" /> Buy 1
            </span>
            <span className="sp-preview-price">$30.00</span>
          </div>
        </div>
        <div className="sp-preview-option sp-preview-option--selected">
          <div className="sp-preview-option__top">
            <span>
              <span className="sp-preview-radio" /> Buy 2, save 10%
            </span>
            <span className="sp-preview-price">$54.00</span>
          </div>
          <div className="sp-help">Most popular. You save $6.00.</div>
        </div>
        <div className="sp-preview-option">
          <div className="sp-preview-option__top">
            <span>
              <span className="sp-preview-radio" /> Buy 3, save 15%
            </span>
            <span className="sp-preview-price">$76.50</span>
          </div>
        </div>
      </div>
      <div className="sp-preview-button sp-preview-button--disabled" aria-disabled="true">
        Add bundle to cart
      </div>
    </div>
  );
}

function OnlineStorePage() {
  return (
    <s-page heading="Online store" inlineSize="large">
      <div className="sp-page-stack">
        <div className="sp-two-column sp-store-layout">
          <OfferPreview />
          <div className="sp-card sp-install-card">
            <div className="sp-section-heading">
              <h3>Theme block</h3>
              <StatusPill>Available</StatusPill>
            </div>
            <p>Add Stackproof inside the product information section.</p>
            <div className="sp-card-actions">
              <s-button variant="primary" href={OFFER_WIDGET_THEME_EDITOR_URL} target="_top">
                Add to product template
              </s-button>
            </div>
          </div>
        </div>
      </div>
    </s-page>
  );
}

function SettingsPage() {
  return (
    <s-page heading="Settings" inlineSize="large">
      <div className="sp-page-stack">
        <div className="sp-settings-layout">
          <section className="sp-card">
            <div className="sp-section-heading sp-section-heading--compact">
              <div>
                <span className="sp-eyebrow">Billing</span>
                <h3>Stackproof Standard</h3>
              </div>
              <StatusPill tone="success">Active</StatusPill>
            </div>
            <div className="sp-plan-row">
              <div>
                <span className="sp-price">$9</span>
                <span className="sp-help"> / month</span>
              </div>
              <span className="sp-help">7-day free trial</span>
            </div>
          </section>
          <section className="sp-card">
            <div className="sp-section-heading sp-section-heading--compact">
              <div>
                <span className="sp-eyebrow">Compatibility</span>
                <h3>Checkout</h3>
              </div>
            </div>
            <div className="sp-detail-list">
              <DetailRow label="Other discounts" value="Not combined" />
              <DetailRow label="Subscription products" value="Not verified" />
            </div>
            <div className="sp-card-note sp-help">Test subscription products before launch.</div>
          </section>
        </div>
      </div>
    </s-page>
  );
}

function DashboardRoute({ onNavigate }: { onNavigate: AppNavigate }) {
  const bundleMergeOwner = useFunctionOwner("truestack-bundle-merge");
  const componentDiscountOwner = useFunctionOwner("truestack-component-discount");
  const bundleSummary = useConfigurationSummary("truestack-bundle-merge", bundleMergeOwner);
  const discountSummary = useConfigurationSummary(
    "truestack-component-discount",
    componentDiscountOwner,
  );
  const state = useMemo(
    () =>
      dashboardReadiness(bundleMergeOwner, componentDiscountOwner, bundleSummary, discountSummary),
    [bundleMergeOwner, componentDiscountOwner, bundleSummary, discountSummary],
  );
  if (state.status === "loading") return <AppLoadingPage route="/" />;
  if (state.status === "error") {
    return (
      <s-page heading="Stackproof Bundles" inlineSize="large">
        <s-section>
          <s-banner tone="critical">Offers could not be loaded. Reload to try again.</s-banner>
          <s-button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </s-button>
        </s-section>
      </s-page>
    );
  }
  return <DashboardPage readiness={state.readiness} onNavigate={onNavigate} />;
}

class OffersRouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <s-page heading="Offers" inlineSize="large">
          <s-section>
            <s-banner tone="critical">
              Offers could not be loaded. Check your connection, then reload.
            </s-banner>
            <s-button variant="primary" onClick={() => reloadOffersRoute(window.location)}>
              Reload
            </s-button>
          </s-section>
        </s-page>
      );
    }
    return this.props.children;
  }
}

function OffersRoute({ onNavigate }: { onNavigate: AppNavigate }) {
  return (
    <OffersRouteErrorBoundary>
      <Suspense fallback={<AppLoadingPage route="/offers" />}>
        <OffersPage onNavigate={onNavigate} />
      </Suspense>
    </OffersRouteErrorBoundary>
  );
}

function ConfiguredApp({ route, onNavigate }: { route: AppRoute; onNavigate: AppNavigate }) {
  if (route === "/") return <DashboardRoute onNavigate={onNavigate} />;
  if (route === "/offers") return <OffersRoute onNavigate={onNavigate} />;
  if (route === "/analytics") return <AnalyticsPage onNavigate={onNavigate} />;
  if (route === "/online-store") return <OnlineStorePage />;
  return <SettingsPage />;
}

function AppLoadingPage({ route }: { route: AppRoute }) {
  const { focusedOffer, heading, showOfferGallery } = loadingPageState(
    route,
    window.location.search,
  );
  return (
    <s-page heading={heading} inlineSize="large">
      <div className="sp-page-stack" aria-label="Loading Stackproof Bundles">
        {route === "/offers" ? (
          focusedOffer ? (
            <>
              <div className="sp-builder-back sp-builder-back--loading" aria-hidden="true">
                <span className="sp-skeleton sp-skeleton--back" />
              </div>
              <OfferManagerSkeleton focused title="Offer builder" />
            </>
          ) : (
            <>
              {showOfferGallery && <OfferTemplateGallerySkeleton />}
              <OfferManagerSkeleton focused={false} title="Bundles and gifts" />
              <OfferManagerSkeleton focused={false} title="Item and quantity discounts" />
            </>
          )
        ) : route === "/analytics" ? (
          <>
            <div className="sp-metric-grid" aria-hidden="true">
              <div className="sp-metric sp-loading-metric" />
              <div className="sp-metric sp-loading-metric" />
              <div className="sp-metric sp-loading-metric" />
            </div>
            <div className="sp-loading-panel sp-loading-panel--analytics" aria-hidden="true" />
          </>
        ) : route === "/online-store" || route === "/settings" ? (
          <div className="sp-two-column" aria-hidden="true">
            <div className="sp-loading-panel sp-loading-panel--detail" />
            <div className="sp-loading-panel sp-loading-panel--detail" />
          </div>
        ) : (
          <>
            <div className="sp-loading-panel sp-loading-panel--home-summary" aria-hidden="true" />
            <div className="sp-loading-panel sp-loading-panel--gallery" aria-hidden="true" />
          </>
        )}
      </div>
    </s-page>
  );
}

function App() {
  const [route, setRoute] = useState<AppRoute>(() =>
    appRouteFromPathname(window.location.pathname),
  );
  const [routeRevision, setRouteRevision] = useState(0);
  const billing = useBilling();
  const navigate = useCallback(
    async (href: string) => {
      const url = new URL(href, window.location.href);
      const nextRoute = appRouteFromPathname(url.pathname);
      try {
        await confirmShopifyLeave();
      } catch {
        return;
      }
      const nextLocation = `${url.pathname}${url.search}${url.hash}`;
      if (nextRoute === route) {
        window.history.replaceState({}, "", nextLocation);
        setRouteRevision((revision) => revision + 1);
      } else {
        window.history.pushState({}, "", nextLocation);
        setRoute(nextRoute);
      }
      window.scrollTo({ top: 0, behavior: "instant" });
    },
    [route],
  );

  useEffect(() => {
    const syncRoute = () => setRoute(appRouteFromPathname(window.location.pathname));
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, []);

  useEffect(() => {
    if (route === "/offers") void import("./OffersPage.js");
  }, [route]);

  let content: ReactNode;
  if (billing.status === "loading") {
    content = <AppLoadingPage route={route} />;
  } else if (billing.status === "subscription-required") {
    content = (
      <s-page heading="Stackproof Bundles">
        <s-section heading="All offer types, one plan">
          <s-banner tone="info">
            Start your 7-day trial, then keep every Stackproof feature for $9 per month.
          </s-banner>
          <s-button variant="primary" href={billing.pricingUrl} target="_top">
            Start free trial
          </s-button>
        </s-section>
      </s-page>
    );
  } else if (billing.status === "error") {
    content = (
      <s-page heading="Stackproof Bundles">
        <s-section>
          <s-banner tone="critical">
            We couldn't verify your subscription. Configuration changes remain unavailable until the
            check succeeds. {billing.message}
          </s-banner>
        </s-section>
      </s-page>
    );
  } else {
    content = (
      <ConfiguredApp key={`${route}-${routeRevision}`} route={route} onNavigate={navigate} />
    );
  }
  return (
    <>
      <AppNavigation onNavigate={navigate} />
      {content}
    </>
  );
}

const container = document.getElementById("app-root");
if (container)
  createRoot(container).render(
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>,
  );
